import React from "react";
import { useActiveOrgId } from "@/lib/org-scope/react";
import { useQuery } from "@tanstack/react-query";
import { BarChart2, CheckCircle2, XCircle, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { testingAxios, testingAuthHeaders } from "@/services/testing";

// ─── API types ──────────────────────────────────────────────────────────────

interface AnalyticsSummary {
  totalPlans: number;
  totalRuns: number;
  passRate7d: number | null;
  passRate7dDetail: { passed: number; total: number };
  passRate30d: number | null;
  passRate30dDetail: { passed: number; total: number };
  failuresByCategory: Array<{ category: string; count: number }>;
  topFailingPlans: Array<{ planId: string; planName: string; failCount: number }>;
  avgRunDurationMs: number | null;
  recentRuns: Array<{
    id: string;
    planId: string;
    planName: string;
    status: string;
    startedAt: string | null;
    durationMs: number | null;
    triageCategory: string | null;
  }>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function passRateColor(rate: number | null): string {
  if (rate == null) return "text-muted-foreground";
  if (rate >= 80) return "text-emerald-400";
  if (rate >= 50) return "text-yellow-400";
  return "text-red-400";
}

function statusBadgeClass(status: string): string {
  if (status === "passed") return "border-emerald-500/40 text-emerald-400 bg-emerald-500/10";
  if (status === "failed") return "border-red-500/40 text-red-400 bg-red-500/10";
  if (status === "running") return "border-primary/40 text-primary bg-primary/10";
  return "border-border/40 text-muted-foreground bg-muted/20";
}

// ─── Main component ──────────────────────────────────────────────────────────

export function TestingAnalytics() {
  // Keyed by organization: `testingAuthHeaders()` sends X-Organization-ID, so
  // this summary is the active org's. Without the id in the key, switching orgs
  // showed the previous org's totals for the full 60s staleTime.
  const orgId = useActiveOrgId();
  const query = useQuery<AnalyticsSummary>({
    queryKey: ["testingAnalyticsSummary", orgId],
    queryFn: async () => {
      const r = await testingAxios.get<AnalyticsSummary>("/api/analytics/summary", {
        headers: testingAuthHeaders(),
      });
      return r.data;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading analytics…
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          {query.error instanceof Error
            ? query.error.message
            : "Failed to load analytics."}
        </span>
      </div>
    );
  }

  const d = query.data;

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Total plans" value={String(d.totalPlans)} icon={BarChart2} />
        <StatTile label="Total runs" value={String(d.totalRuns)} icon={CheckCircle2} />
        <StatTile
          label="Avg run duration"
          value={formatDuration(d.avgRunDurationMs)}
          icon={Clock}
        />
        <StatTile
          label="Pass rate (30d)"
          value={d.passRate30d != null ? `${d.passRate30d}%` : "—"}
          valueClass={passRateColor(d.passRate30d)}
          icon={d.passRate30d != null && d.passRate30d >= 50 ? CheckCircle2 : XCircle}
        />
      </div>

      {/* Pass rate — 7d / 30d tabs */}
      <PassRateSection
        rate7d={d.passRate7d}
        detail7d={d.passRate7dDetail}
        rate30d={d.passRate30d}
        detail30d={d.passRate30dDetail}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Failure categories */}
        <section className="rounded-md border border-border/40 bg-muted/20 p-3">
          <h3 className="mb-2 text-xs font-semibold text-foreground">
            Failure categories (last 30d)
          </h3>
          {d.failuresByCategory.length === 0 ? (
            <p className="text-xs text-muted-foreground">No failures in the last 30 days.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-1 font-medium">Category</th>
                    <th className="pb-1 text-right font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {d.failuresByCategory.map((row) => (
                    <tr key={row.category} className="border-t border-border/20">
                      <td className="py-1 font-mono text-foreground/80">{row.category}</td>
                      <td className="py-1 text-right text-muted-foreground">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Top failing plans */}
        <section className="rounded-md border border-border/40 bg-muted/20 p-3">
          <h3 className="mb-2 text-xs font-semibold text-foreground">Top failing plans</h3>
          {d.topFailingPlans.length === 0 ? (
            <p className="text-xs text-muted-foreground">No failures recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-1 font-medium">Plan</th>
                    <th className="pb-1 text-right font-medium">Failures</th>
                  </tr>
                </thead>
                <tbody>
                  {d.topFailingPlans.map((row) => (
                    <tr key={row.planId} className="border-t border-border/20">
                      <td className="max-w-0 truncate py-1 text-foreground/80" title={row.planName}>
                        {row.planName}
                      </td>
                      <td className="py-1 text-right text-red-400">{row.failCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Recent runs */}
      <section className="rounded-md border border-border/40 bg-muted/20 p-3">
        <h3 className="mb-2 text-xs font-semibold text-foreground">Recent runs</h3>
        {d.recentRuns.length === 0 ? (
          <p className="text-xs text-muted-foreground">No runs yet.</p>
        ) : (
          <div className="space-y-1">
            {d.recentRuns.map((run) => (
              <div
                key={run.id}
                className="flex items-center gap-2 rounded border border-border/20 bg-background/30 px-2 py-1 text-xs"
              >
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] ${statusBadgeClass(run.status)}`}
                >
                  {run.status}
                </span>
                <span className="min-w-0 flex-1 truncate text-foreground/80" title={run.planName}>
                  {run.planName}
                </span>
                {run.triageCategory && (
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {run.triageCategory}
                  </span>
                )}
                <span className="shrink-0 text-muted-foreground">
                  {formatDuration(run.durationMs)}
                </span>
                <span className="shrink-0 text-muted-foreground/60">
                  {relativeTime(run.startedAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  icon: Icon,
  valueClass,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  valueClass?: string;
}) {
  return (
    <div className="rounded-md border border-border/40 bg-muted/20 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${valueClass ?? "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function PassRateSection({
  rate7d,
  detail7d,
  rate30d,
  detail30d,
}: {
  rate7d: number | null;
  detail7d: { passed: number; total: number };
  rate30d: number | null;
  detail30d: { passed: number; total: number };
}) {
  const [tab, setTab] = React.useState<"7d" | "30d">("7d");
  const rate = tab === "7d" ? rate7d : rate30d;
  const detail = tab === "7d" ? detail7d : detail30d;

  return (
    <section className="rounded-md border border-border/40 bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-foreground">Pass rate</h3>
        <div className="flex gap-1">
          {(["7d", "30d"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                tab === t
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/40"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-end gap-4">
        <div className={`text-5xl font-bold tabular-nums ${passRateColor(rate)}`}>
          {rate != null ? `${rate}%` : "—"}
        </div>
        <div className="mb-1 text-xs text-muted-foreground">
          {detail.total > 0 ? (
            <>
              {detail.passed} passed / {detail.total} total
            </>
          ) : (
            "No runs in this window."
          )}
        </div>
      </div>
      {detail.total > 0 && (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted/40">
          <div
            className={`h-full rounded-full transition-all ${
              rate != null && rate >= 80
                ? "bg-emerald-500"
                : rate != null && rate >= 50
                  ? "bg-yellow-500"
                  : "bg-red-500"
            }`}
            style={{ width: `${rate ?? 0}%` }}
          />
        </div>
      )}
    </section>
  );
}

export default TestingAnalytics;
